import React, { useState, useEffect } from "react";
import { styled } from "@mui/material/styles";
import clsx from "clsx";
import { useTranslation } from "react-i18next";
import {
  Container,
  CircularProgress,
  Button,
  Divider,
  FormHelperText,
  Tabs,
  Tab,
  Box,
  Paper,
} from "@mui/material";
import { Typography, TextField } from "@mui/material";
import PropTypes from "prop-types";
import { useForm } from "react-hook-form";
import AddIcon from "@mui/icons-material/Add";
import { guidGenerator, useViewport } from "../../helpers";
import IconButton from "@mui/material/IconButton";
import ClearIcon from "@mui/icons-material/Clear";
import { useDispatch, useSelector } from "react-redux";
import { useSnackbar } from "notistack";
import { createZones } from "../../reducers/myDesigns/myDesignsThunks";

const PREFIX = "MyDesignZones";

const classes = {
  marginBottom: `${PREFIX}-marginBottom`,
  marginTop: `${PREFIX}-marginTop`,
  marginRight: `${PREFIX}-marginRight`,
  addTargetContainer: `${PREFIX}-addTargetContainer`,
  inputSize: `${PREFIX}-inputSize`,
  imageContainer: `${PREFIX}-imageContainer`,
  uploadField: `${PREFIX}-uploadField`,
  paintingContainer: `${PREFIX}-paintingContainer`,
  myDesignImageContainer: `${PREFIX}-myDesignImageContainer`,
  tabs: `${PREFIX}-tabs`,
  positionVertical: `${PREFIX}-positionVertical`,
  displayNone: `${PREFIX}-displayNone`,
  dividerColor: `${PREFIX}-dividerColor`,
  deleteZoneIconButton: `${PREFIX}-deleteZoneIconButton`,
};

const StyledContainer = styled(Container)(({ theme }) => ({
  [`& .${classes.marginBottom}`]: {
    marginBottom: "1rem !important",
  },

  [`& .${classes.marginTop}`]: {
    marginTop: "1rem !important",
  },

  [`& .${classes.marginRight}`]: {
    marginRight: "1rem !important",
  },

  [`& .${classes.addTargetContainer}`]: {
    width: "100%",
    marginTop: "1rem",
    marginBottom: "1rem",
    display: "flex",
  },

  [`& .${classes.inputSize}`]: {
    width: "100%",
    maxWidth: "350px",
  },

  [`& .${classes.imageContainer}`]: {
    width: "100%",
  },

  [`& .${classes.uploadField}`]: {
    maxWidth: "600px",
    margin: "0 auto",
    textAlign: "left",
    display: "flex",
    flexDirection: "row",
  },

  [`& .${classes.paintingContainer}`]: {
    display: "flex",
    flexDirection: "column",
  },

  [`& .${classes.myDesignImageContainer}`]: {
    width: "100%",
    maxWidth: "300px",
    objectFit: "cover",
    margin: "0",
    position: "relative",
  },

  [`& .${classes.tabs}`]: {
    borderRight: `1px solid ${theme.palette.divider}`,
  },

  [`& .${classes.positionVertical}`]: {
    display: "flex",
  },

  [`& .${classes.displayNone}`]: {
    display: "none",
  },

  [`& .${classes.dividerColor}`]: {
    backgroundColor: "rgba(211,175,95,0.6)",
  },

  [`& .${classes.deleteZoneIconButton}`]: {
    position: "absolute",
    right: 0,
    paddingTop: 0,
  },
}));

function TabPanel(props) {
  const { children, value, index, positionType, ...other } = props;

  if (positionType && positionType.length > 0) {
    return (
      <div
        role="tabpanel"
        hidden={value !== index}
        id={`${positionType}-auto-tabpanel-${index}`}
        aria-labelledby={`${positionType}-auto-tab-${index}`}
        {...other}
        style={{ width: "100%" }}
      >
        {value === index && (
          <Box style={{ paddingTop: "12px" }}>
            <div>{children}</div>
          </Box>
        )}
      </div>
    );
  } else {
    return (
      <Paper
        elevation={12}
        role="tabpanel"
        hidden={value !== index}
        id={`full-width-tabpanel-${index}`}
        aria-labelledby={`full-width-tab-${index}`}
        {...other}
      >
        {value === index && (
          <Box style={{ padding: "12px 8px" }}>
            <div>{children}</div>
          </Box>
        )}
      </Paper>
    );
  }
}

TabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.any.isRequired,
  value: PropTypes.any.isRequired,
};

function a11yProps(index, vertical = false) {
  if (vertical) {
    return {
      id: `vertical-tab-${index}`,
      "aria-controls": `vertical-tabpanel-${index}`,
    };
  } else {
    return {
      id: `full-width-tab-${index}`,
      "aria-controls": `full-width-tabpanel-${index}`,
    };
  }
}

const MyDesignZones = (props) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { width } = useViewport();
  const { enqueueSnackbar } = useSnackbar();
  const loading = useSelector((state) => state.myDesigns.loading);
  const {
    register,
    formState: { errors },
    trigger,
    getValues,
    setError,
  } = useForm({
    mode: "onChange",
  });
  const [zones, setZones] = useState([]);
  const [newZoneName, setNewZoneName] = useState("");
  const [activeZone, setActiveZone] = useState(0);
  const [urls, setUrls] = useState({});

  useEffect(() => {
    const urls = {};
    if (props.data && props.data.zones) {
      props.data.zones.forEach((zone) => {
        urls[zone.guid] = URL.createObjectURL(zone.file[0]);
        zone.zones.forEach((zone) => {
          urls[zone.guid] = URL.createObjectURL(zone.file[0]);
        });
      });
      setUrls(urls);
    }
  }, []);

  async function handleSaveNext() {
    const files = [];
    const tmpZones = [...zones];
    tmpZones.forEach((zone) => {
      delete zone.guid;
      if (zone.file) {
        let exist = false;
        files.forEach((file) => {
          if (file[0].name === zone.file[0].name) {
            exist = true;
          }
        });
        if (!exist) {
          files.push(zone.file);
        }
        zone.file = zone.file[0].name.replaceAll(/\s/g, "");
      } else {
        delete zone.file;
      }
    });

    try {
      const thunk = await dispatch(
        createZones({
          frameId: props.frameId,
          zones: tmpZones,
          target: props.selectedTarget.id,
          files,
        })
      );
      if (thunk.meta.requestStatus === "rejected") throw new Error("offline");
      enqueueSnackbar(t("toasts.createZonesSuccess"), { variant: "success" });
    } catch {
      enqueueSnackbar(t("toasts.createZonesError"), { variant: "error" });
    }
  }

  function handleImageReader(event, guid, index, id) {
    let tmpUrls = { ...urls };
    tmpUrls[guid] = URL.createObjectURL(event.target.files[0]);
    let tmpZones = [...zones];
    zones[index].file = getValues(id);
    setZones(tmpZones);
    setUrls(tmpUrls);
  }

  function handleAddZone() {
    let tmpZones = [...zones];
    tmpZones.push({
      name: newZoneName,
      file: null,
      redirect: "",
      guid: guidGenerator(),
    });
    setNewZoneName("");
    setZones(tmpZones);
  }

  function handleZoneChange(value, key, zoneIndex) {
    let tmp = [...zones];
    zones[zoneIndex][key] = value;
    setZones(tmp);
  }

  function zoneAlreadyExist() {
    let exist = false;
    zones.forEach((zone) => {
      if (zone.name === newZoneName) {
        exist = true;
      }
    });
    return exist;
  }

  function handleDeleteZone(zoneIndex) {
    let tmp = [...zones];
    tmp.splice(zoneIndex, 1);
    setZones(tmp);
  }

  return (
    <StyledContainer disableGutters>
      <Typography
        variant="body1"
        className={clsx(classes.marginBottom, classes.marginTop)}
      >
        {t("homePage.myDesign.createForm.addZone")}
      </Typography>
      <div className={classes.addTargetContainer}>
        <TextField
          name="addZoneName"
          label={t("homePage.myDesign.createForm.zoneName")}
          value={newZoneName}
          onChange={(e) => setNewZoneName(e.target.value)}
          className={width > 1024 ? classes.marginRight : ""}
          variant={"outlined"}
        />
        <IconButton
          color="primary"
          aria-label="add zone"
          component="span"
          onClick={() => handleAddZone()}
          disabled={newZoneName.length === 0 || zoneAlreadyExist()}
          size="large"
        >
          <AddIcon />
        </IconButton>
      </div>
      {zoneAlreadyExist() && (
        <Typography variant="body2" style={{ color: "#f44336" }}>
          {t("homePage.myDesign.createForm.errors.zoneNameTaken")}
        </Typography>
      )}
      {zones.length > 0 && (
        <Divider
          light
          className={clsx(classes.marginTop, classes.dividerColor)}
        />
      )}
      <div className={clsx(width > 1024 ? classes.positionVertical : "")}>
        <Tabs
          value={activeZone}
          onChange={(e, value) => setActiveZone(value)}
          indicatorColor="primary"
          textColor="primary"
          variant="scrollable"
          scrollButtons="auto"
          orientation={width > 1024 ? "vertical" : "horizontal"}
          aria-label="scrollable auto tabs example"
          className={
            zones.length === 0
              ? classes.displayNone
              : width > 1024
              ? classes.tabs
              : ""
          }
        >
          {zones.map((zone, index) => (
            <Tab
              key={
                width > 1024
                  ? `vertical-tab-zone-${zone.guid}`
                  : `scrollable-tab-zone-${zone.guid}`
              }
              label={zone.name}
              {...a11yProps(index, true)}
              style={{
                color:
                  errors.hasOwnProperty(`zone-${zone.guid}-image`) && "#f44336",
              }}
            />
          ))}
        </Tabs>
        {zones.map((zone, zoneIndex) => (
          <TabPanel
            key={
              width > 1024
                ? `vertical-tab-zone-${zone.guid}`
                : `scrollable-tabpanel-zone-${zone.guid}`
            }
            value={activeZone}
            index={zoneIndex}
            positionType={width > 1024 ? "vertical" : "scrollable"}
          >
            <div
              style={{
                marginLeft: width > 1024 ? "1rem" : 0,
                marginRight: width > 1024 ? "3rem" : 0,
                display: "flex",
                flexDirection: width > 1024 ? "row" : "column",
                marginTop: "1rem",
              }}
            >
              <div
                className={classes.imageContainer}
                style={{ width: width > 1024 ? "50%" : "100%" }}
              >
                <IconButton
                  color="primary"
                  aria-label="delete zone"
                  component="span"
                  className={classes.deleteZoneIconButton}
                  onClick={() => handleDeleteZone(zoneIndex)}
                  size="large"
                >
                  <ClearIcon />
                </IconButton>
                <Typography
                  variant="h6"
                  className={clsx(urls[zone.guid] ? classes.marginBottom : "")}
                >
                  {t("homePage.myDesign.createForm.choseImage")}
                </Typography>
                <div
                  className={clsx(
                    width <= 1024 ? classes.uploadField : "",
                    urls[zone.guid] ? classes.marginBottom : ""
                  )}
                >
                  <div className={classes.paintingContainer}>
                    <img
                      id={`zone-${zone.guid}-image`}
                      className={classes.myDesignImageContainer}
                      alt={urls[zone.guid] ? `zone ${zoneIndex} image` : null}
                      src={urls[zone.guid] ? urls[zone.guid] : undefined}
                    />
                    <div className={classes.positionContainerContent}>
                      <Button
                        variant="contained"
                        color="primary"
                        component="label"
                        className={clsx(
                          classes.uploadButton,
                          classes.marginTop
                        )}
                        onChange={(e) =>
                          handleImageReader(
                            e,
                            zone.guid,
                            zoneIndex,
                            `zone-${zone.guid}-image`
                          )
                        }
                        onClick={(e) => {
                          e.stopPropagation();
                        }}
                        style={{
                          marginBottom: urls[zone.guid] ? "0.5rem" : "1rem",
                        }}
                      >
                        {t("myDesign.zones.uploadVideo.selectFileButton")}
                        <input
                          type="file"
                          accept="image/*"
                          name={`zone-${zone.guid}-image`}
                          {...register(`zone-${zone.guid}-image`)}
                          hidden
                        />
                      </Button>
                      <Typography
                        variant="body1"
                        className={classes.fileName}
                        style={{
                          marginBottom: zones[zoneIndex].file ? "0.5rem" : 0,
                        }}
                      >
                        {zones[zoneIndex].file
                          ? zones[zoneIndex].file[0].name
                          : ""}
                      </Typography>
                    </div>
                  </div>
                </div>
                {errors.hasOwnProperty(`zone-${zone.guid}-image`) && (
                  <FormHelperText
                    id="targetImageRequired"
                    error
                    className={clsx(
                      classes.negateMarginTop,
                      classes.marginBottom
                    )}
                  >
                    {t("homePage.myDesign.createForm.errors.myDesignImage")}
                  </FormHelperText>
                )}
              </div>
              <div className={classes.inputSize}>
                <TextField
                  name={`zone-redirectUrl-${zone.guid}`}
                  label={t("homePage.myDesign.createForm.redirectUrl")}
                  defaultValue={zone.redirect}
                  onChange={(e) =>
                    handleZoneChange(e.target.value, "redirect", zoneIndex)
                  }
                  className={clsx(classes.marginLeft)}
                  fullWidth
                  variant={"outlined"}
                />
              </div>
            </div>
          </TabPanel>
        ))}
      </div>
      <Divider
        light
        className={clsx(classes.marginBottom, classes.marginTop)}
      />
      <div className={classes.stepperButtonPositions}>
        <Button
          color="primary"
          variant="contained"
          onClick={async () => {
            const result = await trigger();
            if (result) {
              handleSaveNext();
            }
          }}
          className={clsx(classes.marginLeft, classes.marginBottom)}
          disabled={zones.length === 0 || loading}
        >
          {loading ? (
            <CircularProgress size={28} />
          ) : (
            t("homePage.myDesign.createForm.create")
          )}
        </Button>
      </div>
    </StyledContainer>
  );
};

export default MyDesignZones;
